import { SCORES, frequency, scoreBeat, type Soundscape } from "./audio-score";
export type SoundName =
  | "cast"
  | "lance"
  | "plant"
  | "water"
  | "harvest"
  | "awaken"
  | "hit"
  | "hurt"
  | "collect"
  | "dash"
  | "step"
  | "chop"
  | "mine"
  | "guard"
  | "heal"
  | "warning"
  | "craft"
  | "ui";

/** 使用浏览器振荡器合成短音效，部署包不依赖外部音频文件。 */
export class AudioEngine {
  private context: AudioContext | null = null;
  readonly hardMuted =
    typeof location !== "undefined" &&
    new URLSearchParams(location.search).has("muted");
  enabled = false;
  settings = { music: 0.34, ambience: 0.2, sfx: 0.6, muted: true };
  readonly events: Array<{ kind: string; value: string; time: number }> = [];
  private clock = 0;
  private beat = 0;
  private beatTime = 0;
  private scene: Soundscape = "garden";
  private voices = 0;
  private buses: Partial<Record<"music" | "ambience" | "sfx", GainNode>> = {};
  constructor() {
    try {
      const s = JSON.parse(localStorage.getItem("verdant-audio-v1") ?? "{}");
      for (const key of ["music", "ambience", "sfx"] as const)
        if (typeof s[key] === "number" && Number.isFinite(s[key]))
          this.settings[key] = Math.max(0, Math.min(1, s[key]));
      this.settings.muted = s.muted !== false;
    } catch {
      /* unavailable storage leaves safe defaults */
    }
    this.enabled = !this.settings.muted && !this.hardMuted;
  }
  private log(kind: string, value: string) {
    this.events.push({ kind, value, time: this.clock });
    if (this.events.length > 128) this.events.shift();
  }
  setVolume(group: "music" | "ambience" | "sfx", value: number) {
    this.settings[group] = Math.max(0, Math.min(1, value));
    const bus = this.buses[group];
    if (bus && this.context)
      bus.gain.setTargetAtTime(
        this.settings[group],
        this.context.currentTime,
        0.08,
      );
    this.persist();
  }
  private persist() {
    try {
      localStorage.setItem("verdant-audio-v1", JSON.stringify(this.settings));
    } catch {
      /* sound preferences never block play */
    }
  }
  update(dt: number, scene: Soundscape) {
    this.clock += dt;
    if (scene !== this.scene) {
      this.log("transition", `${this.scene}→${scene}:800ms`);
      this.scene = scene;
      this.beat = 0;
      this.beatTime = 0;
      const bus = this.buses.music;
      if (bus && this.context) {
        const now = this.context.currentTime,
          incoming = this.context.createGain();
        bus.gain.cancelScheduledValues(now);
        bus.gain.setValueAtTime(bus.gain.value, now);
        bus.gain.linearRampToValueAtTime(0, now + 0.8);
        incoming.gain.setValueAtTime(0, now);
        incoming.connect(this.context.destination);
        this.buses.music = incoming;
        incoming.gain.linearRampToValueAtTime(this.settings.music, now + 0.8);
        setTimeout(() => bus.disconnect(), 2500);
      }
    }
    this.beatTime -= dt;
    if (this.beatTime <= 0) {
      const step = scoreBeat(this.scene, this.beat++);
      this.beatTime += step.duration;
      this.log("score", `${this.scene}:${step.note}:${step.bass}:${step.drum}`);
      if (this.enabled && !this.hardMuted) {
        if (step.note)
          this.tone(
            frequency(step.note),
            step.duration * 1.7,
            "triangle",
            0.035,
            "music",
          );
        if (step.bass)
          this.tone(
            frequency(step.bass),
            step.duration * 4,
            "sine",
            0.045,
            "music",
          );
        if (step.drum) this.tone(60, 0.12, "triangle", 0.045, "music");
        if (this.beat % 8 === 1)
          this.wind(step.duration * 8, SCORES[scene].ambience);
      }
    }
  }
  /** Deterministic filtered air texture; generated locally, no recorded samples. */
  private wind(duration: number, hz: number) {
    if (this.hardMuted || !this.enabled || !this.context || this.voices >= 24)
      return;
    const c = this.context,
      buffer = c.createBuffer(1, Math.floor(c.sampleRate * 0.5), c.sampleRate),
      data = buffer.getChannelData(0);
    let seed = 73,
      previous = 0;
    for (let i = 0; i < data.length; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      previous = previous * 0.96 + ((seed / 4294967296) * 2 - 1) * 0.04;
      data[i] = previous;
    }
    const source = c.createBufferSource(),
      filter = c.createBiquadFilter(),
      gain = c.createGain(),
      now = c.currentTime;
    let bus = this.buses.ambience;
    if (!bus) {
      bus = c.createGain();
      bus.gain.value = this.settings.ambience;
      bus.connect(c.destination);
      this.buses.ambience = bus;
    }
    source.buffer = buffer;
    source.loop = true;
    filter.type = "lowpass";
    filter.frequency.value = hz * 8;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.16, now + 0.3);
    gain.gain.linearRampToValueAtTime(0.0001, now + duration);
    source.connect(filter).connect(gain).connect(bus);
    this.voices++;
    source.onended = () => {
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
      this.voices--;
    };
    source.start(now);
    source.stop(now + duration);
  }
  private tone(
    hz: number,
    duration: number,
    type: OscillatorType,
    level: number,
    group: "music" | "ambience" | "sfx",
  ) {
    if (this.hardMuted || !this.enabled || !this.context || this.voices >= 24)
      return;
    const c = this.context,
      osc = c.createOscillator(),
      g = c.createGain(),
      now = c.currentTime;
    let bus = this.buses[group];
    if (!bus) {
      bus = c.createGain();
      bus.gain.value = this.settings[group];
      bus.connect(c.destination);
      this.buses[group] = bus;
    }
    osc.type = type;
    osc.frequency.value = hz;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(level, now + 0.025);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(g).connect(bus);
    this.voices++;
    osc.onended = () => {
      osc.disconnect();
      g.disconnect();
      this.voices--;
    };
    osc.start(now);
    osc.stop(now + duration);
  }

  unlock(): void {
    if (!this.enabled || this.hardMuted) return;
    // 声音初始化失败不能阻断开始游戏或触控手势。
    try {
      this.context ??= new AudioContext();
      if (this.context.state === "suspended")
        void this.context.resume().catch(() => {});
    } catch {
      this.enabled = false;
    }
  }

  toggle(): boolean {
    if (this.hardMuted) return false;
    this.enabled = !this.enabled;
    this.settings.muted = !this.enabled;
    this.persist();
    if (!this.enabled && this.context)
      void this.context.suspend().catch(() => {});
    if (this.enabled) this.unlock();
    return this.enabled;
  }

  play(name: SoundName): void {
    this.log("sfx", name);
    if (!this.enabled) return;
    this.unlock();
    if (!this.context || this.hardMuted) return;

    const presets: Record<SoundName, [number, number, number, OscillatorType]> =
      {
        cast: [760, 280, 0.13, "triangle"],
        lance: [210, 960, 0.24, "triangle"],
        plant: [330, 510, 0.14, "sine"],
        water: [520, 310, 0.18, "sine"],
        harvest: [420, 760, 0.2, "triangle"],
        awaken: [260, 880, 0.46, "sine"],
        hit: [150, 78, 0.09, "square"],
        hurt: [190, 110, 0.14, "sawtooth"],
        collect: [620, 920, 0.1, "triangle"],
        dash: [220, 90, 0.12, "sine"],
        ui: [440, 520, 0.06, "sine"],
        step: [95, 130, 0.08, "triangle"],
        chop: [125, 240, 0.15, "triangle"],
        mine: [920, 1380, 0.28, "sine"],
        guard: [280, 420, 0.18, "triangle"],
        heal: [660, 990, 0.4, "sine"],
        warning: [180, 220, 0.25, "triangle"],
        craft: [380, 760, 0.24, "triangle"],
      };
    const [from, to, duration, type] = presets[name];
    this.tone(from, duration, type, 0.065, "sfx");
    this.tone(to, duration * 1.25, "sine", 0.024, "sfx");
  }
}
